import CryptoJS from "crypto-js";

// Resolves the current client's logo, preferring the centrally hosted copy at
//   s3://cmp.io/images/<clientName>.png
// and falling back to the copy bundled in this repo's public/images folder.
//
// The point of the remote copy is that a new client logo can be dropped into
// s3://cmp.io/images/ without a commit and a redeploy of this app.
//
// The cmp.io bucket is public-read (PublicReadGetObject, HTTPS-only) and fronted
// by CloudFront at https://cmp.io, so the logo is a plain <img> source - no
// presigning and no CORS involved. Note the bucket name contains dots, so the
// S3 REST endpoint (cmp.io.s3.<region>.amazonaws.com) fails TLS validation
// against the *.s3.<region>.amazonaws.com wildcard cert; the custom domain is
// the only usable host.
//
// IMPORTANT: a missing key does NOT 404. cmp.io serves this app, so CloudFront
// answers an unknown path with index.html and HTTP 200 (content-type
// text/html). Existence therefore cannot be inferred from the status code - the
// URL has to be decoded as an actual image, which is what imageLoads() does.

const DEFAULT_LOGO_BASE_URL = "https://cmp.io";
const LOGO_PATH = "/images/";

function getLogoBaseUrl() {
  const base = process.env.REACT_APP_LOGO_BASE_URL;
  // Trailing slashes would double up against LOGO_PATH.
  return (base ? base.trim() : DEFAULT_LOGO_BASE_URL).replace(/\/+$/, "");
}

// The concept name is what the bundled /images/<name>.png lookup has always
// keyed off, so it stays the source of truth for the remote copy too.
export function getClientName() {
  const encrypted = localStorage.getItem("conceptName");
  if (!encrypted) return "";
  try {
    return JSON.parse(
      CryptoJS.AES.decrypt(encrypted, process.env.REACT_APP_PASS).toString(
        CryptoJS.enc.Utf8
      )
    );
  } catch (e) {
    console.log("Unable to resolve client name for logo", e);
    return "";
  }
}

// The pre-existing behaviour: an asset committed to public/images and served
// from this app's own origin. Kept as the fallback so a client with no logo in
// s3://cmp.io renders exactly as it did before.
export function getBundledClientLogoUrl(clientName) {
  const name = clientName === undefined ? getClientName() : clientName;
  if (!name) return "";
  return LOGO_PATH + encodeURIComponent(name) + ".png";
}

// Candidate remote URLs, most specific first. Concept-name casing is not
// consistent (public/images holds both `benihana.png` and `Benihana.png`, while
// everything in s3://cmp.io/images is lowercase), so the lowercased spelling is
// tried as well before giving up on the remote copy.
export function getRemoteClientLogoUrls(clientName) {
  const name = clientName === undefined ? getClientName() : clientName;
  if (!name) return [];

  const base = getLogoBaseUrl();
  const urls = [base + LOGO_PATH + encodeURIComponent(name) + ".png"];

  const lowerName = name.toLowerCase();
  if (lowerName !== name) {
    urls.push(base + LOGO_PATH + encodeURIComponent(lowerName) + ".png");
  }
  return urls.filter((url, idx) => urls.indexOf(url) === idx);
}

// Resolves true only if the URL decodes as an image. This is what
// distinguishes a real logo from the index.html that cmp.io returns with a 200
// for keys that do not exist.
function imageLoads(url) {
  return new Promise((resolve) => {
    const probe = new Image();
    probe.onload = () => resolve(true);
    probe.onerror = () => resolve(false);
    probe.src = url;
  });
}

// Resolves to a usable remote logo URL, or null when the client has no logo in
// s3://cmp.io (in which case the caller should stay on the bundled asset).
export async function resolveClientLogoUrl(clientName) {
  const urls = getRemoteClientLogoUrls(clientName);
  for (const url of urls) {
    if (await imageLoads(url)) {
      return url;
    }
  }
  return null;
}

export default resolveClientLogoUrl;



// WEBPACK FOOTER //
// ./src/util/clientLogo/clientLogo.js